import type { ExtractComponentProp } from '@approvalmax/types';
import { Popup, WarningImage } from '@approvalmax/ui';
import { Button, Grid, Link, SimpleLayout, Text, useConfirmation, useLayout } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { ContactSupportPopup } from 'modules/profile';
import { useSelector } from 'modules/react-redux';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { storageService } from 'services/storage';
import { companyIdQueryParam } from 'shared/constants';
import { getPath, Path } from 'urlBuilder';

import { messages } from './resources/messages';

const Page = () => {
    useLayout({ containerMinSize: 'xxsmall' });

    const history = useHistory();

    const [isContactSupportPopupOpen, setIsContactSupportPopupOpen] = useState(false);

    const [searchParams] = hooks.useUrlSearchParams();
    const companyId = searchParams.get(companyIdQueryParam);
    const consentId = searchParams.get('consent-id');
    const consentType = searchParams.get('consent-type');
    // should be equal to companyId
    const organizationId = searchParams.get('organization-id');
    const institutionId = searchParams.get('institution-id');
    const userInfo = searchParams.get('user-info');
    const errorCode = searchParams.get('error-code');
    const error = searchParams.get('error');
    const me = useSelector(selectors.profile.findProfileUser);

    // 'null' can be returned when user cancels the process of connection
    // BA on the bank side
    const bankAccountLink = `${getPath(Path.bankAccounts, companyId ?? '')}${consentId ? `?consent-id=${consentId}` : ''}`;

    const isAuthenticated = storageService.authenticated();

    const { confirmation } = useConfirmation({
        confirmMessage: messages.areYouSure,
        confirmButtonMessage: messages.yes,
    });

    const transformMessage = useCallback(
        (message: string) =>
            JSON.stringify({
                message,
                companyId,
                organizationId,
                institutionId,
                consentId,
                consentType,
                errorCode,
                error,
                userId: me?.id,
                userInfo,
            }),
        [companyId, consentId, consentType, error, errorCode, institutionId, me?.id, organizationId, userInfo]
    );

    const openContactSupportPopup = useCallback(() => {
        setIsContactSupportPopupOpen(true);
    }, []);

    const closeContactSupportPopup = useCallback<ExtractComponentProp<typeof Popup, 'onRequestClose'>>(
        (e, reason) => {
            if (reason) {
                void confirmation()
                    .then(() => {
                        setIsContactSupportPopupOpen(false);
                    })
                    .catch(() => {});
            }
        },
        [confirmation]
    );

    const handleSend = useCallback(() => {
        history.push(bankAccountLink);
    }, [bankAccountLink, history]);

    return (
        <SimpleLayout size='small'>
            <Helmet title={messages.title} />

            <SimpleLayout.Content>
                <Grid gap={24} justifyItems='center'>
                    <WarningImage />

                    <Text font='headline' fontSize='small' fontWeight='medium' textAlign='center'>
                        {messages.unableToEstablish}
                    </Text>

                    <Text font='body' fontSize='medium' textAlign='center'>
                        {messages.thereWasAnIssue}
                    </Text>

                    <Link
                        external
                        font='label'
                        fontSize='medium'
                        textAlign='center'
                        href='https://support.approvalmax.com/portal/en/kb/articles/why-my-bank-account-connection-has-failed'
                    >
                        {messages.learnMore}
                    </Link>

                    {isAuthenticated && (
                        <Grid gap={24} gridAutoFlow='column'>
                            <Button variant='outline' color='blue80' size='large' onClick={openContactSupportPopup}>
                                {messages.contactSupport}
                            </Button>

                            <Button as={RouterLink} to={bankAccountLink} color='blue80' size='large'>
                                {messages.tryAgain}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </SimpleLayout.Content>

            <Popup isOpen={isContactSupportPopupOpen} onRequestClose={closeContactSupportPopup}>
                <ContactSupportPopup transformMessage={transformMessage} onSend={handleSend} />
            </Popup>
        </SimpleLayout>
    );
};

Page.displayName = 'Page';

export default Page;
